// src/modules/Orders/OrderReturnAnalysisPage.js
// VERSION: 1.1 - Retorno de Pedido de Venda para Análise com suporte a edição/exclusão

import React, { useState, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import useAuth from '../../hooks/useAuth';
import usePagePermissions from '../../hooks/usePagePermissions';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNotifications } from '../../contexts/NotificationContext';
import { getModuleConfig } from '../../config/notificationConfig';
import Toast from '../../components/common/Toast/Toast';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import styles from './Orders.module.css';

// React Icons
import {
    FiSearch,
    FiRefreshCw,
    FiX,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiCalendar,
    FiUser,
    FiPackage,
    FiDollarSign,
    FiClock,
    FiArrowLeft,
    FiBox,
    FiTruck
} from 'react-icons/fi';
import { MdBusiness } from 'react-icons/md';

// ============================================
// 1. CONSTANTES E DEFINIÇÕES
// ============================================
const SALES_ORDERS_COLLECTION = 'sales-orders';
const CLIENTS_COLLECTION = 'clients';

// Status do Pedido de Venda
const ORDER_STATUS = {
    DRAFT: 'draft',
    PENDING: 'pending',
    APPROVED: 'approved',
    IN_PRODUCTION: 'in_production',
    SHIPPED: 'shipped',
    DELIVERED: 'delivered',
    CANCELLED: 'cancelled',
    RETURNED_FOR_ANALYSIS: 'returned_for_analysis'
};

const ORDER_STATUS_LABELS = {
    [ORDER_STATUS.DRAFT]: { label: 'Rascunho', className: 'statusBadgeDraft' },
    [ORDER_STATUS.PENDING]: { label: 'Pendente Aprovação', className: 'statusBadgePending' },
    [ORDER_STATUS.APPROVED]: { label: 'Aprovado', className: 'statusBadgeApproved' },
    [ORDER_STATUS.IN_PRODUCTION]: { label: 'Em Produção', className: 'statusBadgeInfo' },
    [ORDER_STATUS.SHIPPED]: { label: 'Enviado', className: 'statusBadgeInfo' },
    [ORDER_STATUS.DELIVERED]: { label: 'Entregue', className: 'statusBadgeCompleted' },
    [ORDER_STATUS.CANCELLED]: { label: 'Cancelado', className: 'statusBadgeCancelled' },
    [ORDER_STATUS.RETURNED_FOR_ANALYSIS]: { label: 'Retornado para Análise', className: 'statusBadgeWarning' }
};

// Status que podem ser retornados para análise
const RETURNABLE_STATUSES = [
    ORDER_STATUS.PENDING,
    ORDER_STATUS.APPROVED,
    ORDER_STATUS.IN_PRODUCTION,
    ORDER_STATUS.SHIPPED,
    ORDER_STATUS.DELIVERED
];

const formatCurrency = (value) => {
    if (!value && value !== 0) return 'R$ 0,00';
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
};

const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString('pt-BR');
    return new Date(dateValue).toLocaleDateString('pt-BR');
};

// ============================================
// 2. MAIN COMPONENT
// ============================================
const OrderReturnAnalysisPage = () => {
    const can = usePagePermissions('pedido_de_venda');
    const { currentUser } = useAuth();
    const { addNotification } = useNotifications();

    // ============================================
    // 2.1 ESTADOS
    // ============================================
    const [searchCode, setSearchCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [orderNotFound, setOrderNotFound] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [formStatus, setFormStatus] = useState({ message: '', type: '' });
    const [analysisReason, setAnalysisReason] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [orderHistory, setOrderHistory] = useState([]);
    const [clientData, setClientData] = useState(null);

    const { refreshData: refreshOrders } = useFirestore(SALES_ORDERS_COLLECTION);

    // ============================================
    // 2.2 TOAST FUNCTIONS
    // ============================================
    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 5000);
    };

    // ============================================
    // 2.3 BUSCAR PEDIDO POR CÓDIGO
    // ============================================
    const searchOrderByCode = useCallback(async () => {
        if (!searchCode.trim()) {
            setFormStatus({ message: 'Digite o código do pedido para buscar.', type: 'warning' });
            return;
        }

        setIsLoading(true);
        setOrderNotFound(false);
        setOrderData(null);
        setClientData(null);
        setOrderHistory([]);
        setFormStatus({ message: '', type: '' });

        try {
            // Buscar pedido pelo código
            const ordersRef = collection(db, SALES_ORDERS_COLLECTION);
            const q = query(ordersRef, where('code', '==', searchCode.trim().toUpperCase()));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                setOrderNotFound(true);
                setFormStatus({ message: `Pedido ${searchCode} não encontrado.`, type: 'error' });
                return;
            }

            const docSnap = querySnapshot.docs[0];
            const order = {
                id: docSnap.id,
                ...docSnap.data()
            };

            // Verificar se o pedido pode ser retornado para análise
            if (!RETURNABLE_STATUSES.includes(order.status)) {
                const statusLabel = ORDER_STATUS_LABELS[order.status]?.label || order.status;
                // Check if it's already returned for analysis
                if (order.status === ORDER_STATUS.RETURNED_FOR_ANALYSIS) {
                    setOrderNotFound(true);
                    setFormStatus({
                        message: `O pedido ${order.code} já está com status "Retornado para Análise". Use a página de Pedidos de Venda para editá-lo ou excluí-lo.`,
                        type: 'warning'
                    });
                    return;
                }
                setOrderNotFound(true);
                setFormStatus({
                    message: `O pedido ${order.code} está com status "${statusLabel}" e não pode ser retornado para análise.`,
                    type: 'error'
                });
                return;
            }

            setOrderData(order);

            // Buscar dados do cliente
            if (order.clientId) {
                const clientDoc = await getDoc(doc(db, CLIENTS_COLLECTION, order.clientId));
                if (clientDoc.exists()) {
                    setClientData({ id: clientDoc.id, ...clientDoc.data() });
                }
            }

            // Buscar histórico de tracking
            if (order.trackingHistory && Array.isArray(order.trackingHistory)) {
                setOrderHistory(order.trackingHistory);
            }

            setFormStatus({
                message: `Pedido ${order.code} encontrado! Status atual: ${ORDER_STATUS_LABELS[order.status]?.label || order.status}`,
                type: 'success'
            });

            // Preencher razão com informações do pedido
            setAnalysisReason(`Análise solicitada para o pedido ${order.code} - ${order.clientName || ''}`);

        } catch (error) {
            console.error('Erro ao buscar pedido:', error);
            setFormStatus({ message: `Erro ao buscar pedido: ${error.message}`, type: 'error' });
            setOrderNotFound(true);
        } finally {
            setIsLoading(false);
        }
    }, [searchCode]);

    // ============================================
    // 2.4 NOTIFICAR CRIADOR SOBRE RETORNO PARA ANÁLISE
    // ============================================
    const notifyOrderCreator = async (orderId, orderCode, reason) => {
        try {
            const orderDoc = await getDoc(doc(db, 'sales-orders', orderId));
            if (!orderDoc.exists()) return;

            const orderData = orderDoc.data();
            const creatorName = orderData.responsible;

            if (!creatorName) return;

            const usersSnapshot = await getDocs(collection(db, 'users'));
            let creatorUser = null;

            usersSnapshot.forEach(doc => {
                const data = doc.data();
                if (data.nome === creatorName || data.email === creatorName) {
                    creatorUser = { uid: doc.id, ...data };
                }
            });

            if (!creatorUser || creatorUser.uid === currentUser?.uid) return;

            const config = getModuleConfig('pedido_de_venda');

            const notification = {
                id: `so_returned_${orderId}_${Date.now()}`,
                type: 'sales_order_returned_for_analysis',
                module: 'pedido_de_venda',
                moduleIcon: config?.icon || '📄',
                moduleColor: config?.color || '#f39c12',
                moduleName: config?.name || 'Pedido de Venda',
                title: '🔄 Pedido Retornado para Análise',
                message: `Seu pedido ${orderCode} foi retornado para análise por ${currentUser?.nome || 'Aprovador'}`,
                fullMessage: `Detalhes:\n• Pedido: ${orderCode}\n• Status: Retornado para Análise\n• Responsável: ${currentUser?.nome || 'Aprovador'}\n• Motivo: ${reason || 'Análise solicitada'}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: 'Alta',
                route: `/pedidos/pedido-de-venda`,
                data: {
                    orderId: orderId,
                    orderCode: orderCode,
                    reason: reason || 'Análise solicitada',
                    returnedBy: currentUser?.nome || 'Aprovador'
                },
                userId: creatorUser.uid
            };

            addNotification(notification);

            try {
                await addDoc(collection(db, 'notifications'), {
                    userId: creatorUser.uid,
                    ...notification,
                    createdAt: new Date()
                });
            } catch (err) {
                console.warn('Erro ao salvar notificação:', err);
            }
        } catch (error) {
            console.error('❌ Erro ao notificar criador:', error);
        }
    };

    // ============================================
    // 2.5 NOTIFICAR APROVADORES SOBRE RETORNO
    // ============================================
    const notifyApprovers = async (orderId, orderCode, reason) => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const targetUsers = [];

            usersSnapshot.forEach(doc => {
                const userData = doc.data();
                const roles = Array.isArray(userData.role) ? userData.role : [userData.role];

                if (roles.some(r => r === 'Diretoria' || r === 'Diretoria (Admin)' || r === 'Financeiro')) {
                    targetUsers.push({
                        uid: doc.id,
                        ...userData
                    });
                }
            });

            const config = getModuleConfig('pedido_de_venda');

            const baseNotification = {
                type: 'sales_order_returned_for_analysis',
                module: 'pedido_de_venda',
                moduleIcon: config?.icon || '📄',
                moduleColor: config?.color || '#f39c12',
                moduleName: config?.name || 'Pedido de Venda',
                title: '🔄 Pedido Retornado para Análise',
                fullMessage: `O pedido ${orderCode} foi retornado para análise.\nMotivo: ${reason || 'Análise solicitada'}\nResponsável: ${currentUser?.nome || 'Aprovador'}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: 'Alta',
                route: `/pedidos/pedido-de-venda`,
                data: {
                    orderId: orderId,
                    orderCode: orderCode,
                    reason: reason || 'Análise solicitada',
                    returnedBy: currentUser?.nome || 'Aprovador'
                }
            };

            for (const user of targetUsers) {
                if (user.uid === currentUser?.uid) continue;

                addNotification({
                    ...baseNotification,
                    id: `so_returned_${orderId}_${user.uid}_${Date.now()}`,
                    message: `${user.nome || 'Usuário'}, o pedido ${orderCode} foi retornado para análise por ${currentUser?.nome || 'Alguém'}`,
                    userId: user.uid
                });

                try {
                    await addDoc(collection(db, 'notifications'), {
                        userId: user.uid,
                        ...baseNotification,
                        id: `so_returned_${orderId}_${user.uid}_${Date.now()}`,
                        message: `${user.nome || 'Usuário'}, o pedido ${orderCode} foi retornado para análise por ${currentUser?.nome || 'Alguém'}`,
                        createdAt: new Date()
                    });
                } catch (err) {
                    console.warn('Erro ao salvar notificação:', err);
                }
            }
        } catch (error) {
            console.error('❌ Erro ao notificar aprovadores:', error);
        }
    };

    // ============================================
    // 2.6 CONFIRMAR RETORNO PARA ANÁLISE
    // ============================================
    const confirmReturnForAnalysis = async () => {
        if (!orderData) {
            addToast('Nenhum pedido selecionado para retorno.', 'Error');
            return;
        }

        if (!analysisReason.trim()) {
            setFormStatus({ message: 'Por favor, informe o motivo do retorno para análise.', type: 'warning' });
            return;
        }

        setIsSubmitting(true);
        setFormStatus({ message: '', type: '' });

        try {
            const orderRef = doc(db, SALES_ORDERS_COLLECTION, orderData.id);

            // Obter histórico existente
            const existingHistory = orderData.trackingHistory || [];

            // Criar entrada no histórico
            const newHistoryEntry = {
                date: new Date().toLocaleDateString('pt-BR'),
                status: 'Retornado para Análise',
                description: `Pedido retornado para análise. Motivo: ${analysisReason}`,
                updatedBy: currentUser?.nome || 'Sistema'
            };

            // Atualizar o pedido
            await updateDoc(orderRef, {
                status: ORDER_STATUS.RETURNED_FOR_ANALYSIS,
                returnedForAnalysisAt: new Date(),
                returnedForAnalysisBy: currentUser?.nome || 'Sistema',
                returnedForAnalysisReason: analysisReason,
                trackingHistory: [...existingHistory, newHistoryEntry],
                updatedAt: new Date()
            });

            // ✅ NOTIFICAR CRIADOR
            await notifyOrderCreator(orderData.id, orderData.code, analysisReason);

            // ✅ NOTIFICAR APROVADORES
            await notifyApprovers(orderData.id, orderData.code, analysisReason);

            addToast(`Pedido ${orderData.code} retornado para análise com sucesso!`, 'Success');

            // Atualizar dados localmente
            setOrderData({
                ...orderData,
                status: ORDER_STATUS.RETURNED_FOR_ANALYSIS
            });

            // Atualizar histórico
            setOrderHistory([...existingHistory, newHistoryEntry]);

            // Fechar modal
            setShowConfirmModal(false);

            // Mostrar mensagem informativa sobre edição/exclusão
            setFormStatus({
                message: `✅ Pedido ${orderData.code} retornado para análise. Agora você pode editá-lo ou excluí-lo na página de Pedidos de Venda.`,
                type: 'success'
            });

            // Refrescar dados
            await refreshOrders();

        } catch (error) {
            console.error('Erro ao retornar pedido para análise:', error);
            setFormStatus({ message: `Erro ao retornar pedido: ${error.message}`, type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // ============================================
    // 2.7 HANDLE ENTER KEY PRESS
    // ============================================
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            searchOrderByCode();
        }
    };

    // ============================================
    // 2.8 RENDERIZAÇÃO DO MODAL DE CONFIRMAÇÃO
    // ============================================
    const renderConfirmModal = () => {
        if (!showConfirmModal) return null;

        return (
            <div className={styles.modalBackdrop}>
                <div className={`${styles.formContainer} ${styles.formContainerSmall}`}>
                    <div className={styles.modalHeader}>
                        <h3>Confirmar Retorno para Análise</h3>
                        <button
                            onClick={() => setShowConfirmModal(false)}
                            className={styles.modalCloseButton}
                            disabled={isSubmitting}
                        >
                            <FiX size={24} />
                        </button>
                    </div>
                    <div className={styles.formContent}>
                        <div style={{ textAlign: 'center', padding: '8px 0' }}>
                            <FiRefreshCw size={48} style={{ color: '#f39c12' }} />
                        </div>
                        <p style={{ fontSize: '18px', textAlign: 'center', marginBottom: '8px' }}>
                            Tem certeza que deseja retornar o pedido <strong>{orderData?.code}</strong> para análise?
                        </p>
                        <p style={{ textAlign: 'center', color: 'var(--orders-text-secondary)', marginBottom: '16px' }}>
                            O pedido será movido para o status <strong>"Retornado para Análise"</strong> e o criador será notificado.
                        </p>
                        <div style={{
                            background: 'var(--orders-bg-secondary)',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '12px'
                        }}>
                            <strong>Motivo informado:</strong>
                            <p style={{ margin: '4px 0 0 0', color: 'var(--orders-text-secondary)' }}>
                                {analysisReason || 'Nenhum motivo informado.'}
                            </p>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px',
                            background: '#fff8e1',
                            borderRadius: '8px',
                            color: '#856404',
                            fontSize: '14px'
                        }}>
                            <FiAlertCircle size={18} />
                            <span>Esta ação não pode ser desfeita automaticamente.</span>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px',
                            background: '#e3f2fd',
                            borderRadius: '8px',
                            color: '#0d47a1',
                            fontSize: '14px',
                            marginTop: '8px'
                        }}>
                            <FiInfo size={18} />
                            <span>Após o retorno, o pedido poderá ser <strong>editado</strong> ou <strong>excluído</strong> na página de Pedidos de Venda.</span>
                        </div>
                    </div>
                    <div className={styles.formActions}>
                        <button
                            onClick={() => setShowConfirmModal(false)}
                            className={styles.secondaryButton}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={confirmReturnForAnalysis}
                            className={styles.primaryButton}
                            disabled={isSubmitting || !analysisReason.trim()}
                        >
                            {isSubmitting ? (
                                <>
                                    <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                    Processando...
                                </>
                            ) : (
                                <>
                                    <FiCheck size={18} /> Confirmar Retorno
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================
    // 2.9 RENDERIZAÇÃO DOS DETALHES DO PEDIDO
    // ============================================
    const renderOrderDetails = () => {
        if (!orderData) return null;

        const statusLabel = ORDER_STATUS_LABELS[orderData.status]?.label || orderData.status;

        return (
            <div className={styles.orderDetailsContainer}>
                {/* Informações Gerais */}
                <div className={styles.detailsHeader}>
                    <div className={styles.detailsTitle}>
                        <h3>Pedido {orderData.code}</h3>
                        <StatusBadge status={orderData.status} />
                    </div>
                    <div className={styles.detailsMeta}>
                        <span><FiCalendar /> {formatDate(orderData.createdAt)}</span>
                        <span><FiUser /> {orderData.responsible || '-'}</span>
                    </div>
                </div>

                {/* Informações do Cliente */}
                <div className={styles.detailsSection}>
                    <h4><MdBusiness /> Cliente</h4>
                    <div className={styles.detailsGrid}>
                        <div className={styles.detailsItem}>
                            <span className={styles.detailsLabel}>Nome:</span>
                            <span className={styles.detailsValue}>{orderData.clientName || '-'}</span>
                        </div>
                        {clientData && (
                            <>
                                <div className={styles.detailsItem}>
                                    <span className={styles.detailsLabel}>Código:</span>
                                    <span className={styles.detailsValue}>{clientData.codigo || '-'}</span>
                                </div>
                                <div className={styles.detailsItem}>
                                    <span className={styles.detailsLabel}>Email:</span>
                                    <span className={styles.detailsValue}>{clientData.email || clientData.emailUsuario || '-'}</span>
                                </div>
                            </>
                        )}
                        <div className={styles.detailsItem}>
                            <span className={styles.detailsLabel}>Executivo:</span>
                            <span className={styles.detailsValue}>{orderData.accountExecutiveName || '-'}</span>
                        </div>
                        <div className={styles.detailsItem}>
                            <span className={styles.detailsLabel}>Endereço Entrega:</span>
                            <span className={styles.detailsValue}>{orderData.deliveryAddress || '-'}</span>
                        </div>
                        {orderData.palletTypeId && (
                            <div className={styles.detailsItem}>
                                <span className={styles.detailsLabel}>Tipo de Palete:</span>
                                <span className={styles.detailsValue}>{orderData.palletTypeId}</span>
                            </div>
                        )}
                        {orderData.useImporter && (
                            <>
                                <div className={styles.detailsItem}>
                                    <span className={styles.detailsLabel}>Importador:</span>
                                    <span className={styles.detailsValue}>{orderData.importerCompanyName || '-'}</span>
                                </div>
                                <div className={styles.detailsItem}>
                                    <span className={styles.detailsLabel}>End. Importador:</span>
                                    <span className={styles.detailsValue}>{orderData.importerAddress || '-'}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Produtos */}
                <div className={styles.detailsSection}>
                    <h4><FiPackage /> Produtos</h4>
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Código</th>
                                    <th>Produto</th>
                                    <th className={styles.textRight}>Qtde</th>
                                    <th className={styles.textRight}>Preço Unit.</th>
                                    <th className={styles.textRight}>Total</th>
                                    <th>Caixa</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orderData.items && orderData.items.length > 0 ? (
                                    orderData.items.map((item, idx) => {
                                        const boxName = item.availableBoxTypes?.find(b => b.boxTypeId === item.selectedBoxTypeId)?.boxName ||
                                            item.selectedBoxTypeId ||
                                            (item.availableBoxTypes?.length > 0 ? item.availableBoxTypes[0].boxName : '');
                                        return (
                                            <tr key={idx} className={item.isBonus ? styles.bonusRow : ''}>
                                                <td>{idx + 1}</td>
                                                <td>{item.code || '-'}</td>
                                                <td>{item.name || '-'}{item.isBonus ? ' (BÔNUS)' : ''}</td>
                                                <td className={styles.textRight}>{item.quantity || 0}</td>
                                                <td className={styles.textRight}>{formatCurrency(item.unitPrice)}</td>
                                                <td className={styles.textRight}>{formatCurrency(item.total)}</td>
                                                <td>{boxName || '-'}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="7" className={styles.emptyTable}>Nenhum produto</td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr className={styles.tableFooterRow}>
                                    <td colSpan="5" className={styles.textRight}><strong>Valor Total:</strong></td>
                                    <td className={styles.textRight}>
                                        <strong>{formatCurrency(orderData.calculatedValues?.total)}</strong>
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Histórico de Tracking */}
                {orderHistory.length > 0 && (
                    <div className={styles.detailsSection}>
                        <h4><FiClock /> Histórico de Status</h4>
                        <div className={styles.trackingHistory}>
                            {orderHistory.map((entry, idx) => (
                                <div key={idx} className={styles.trackingEvent}>
                                    <div className={styles.trackingDate}>{entry.date}</div>
                                    <div className={styles.trackingStatus}>{entry.status}</div>
                                    <div className={styles.trackingDescription}>
                                        {entry.description}
                                        {entry.updatedBy && <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--orders-text-muted)' }}>Por: {entry.updatedBy}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Motivo e Botões */}
                <div className={styles.reasonField}>
                    <label htmlFor="analysisReason">
                        Motivo do Retorno para Análise <span className={styles.required}>*</span>
                    </label>
                    <textarea
                        id="analysisReason"
                        value={analysisReason}
                        onChange={(e) => setAnalysisReason(e.target.value)}
                        className={styles.textarea}
                        rows={3}
                        placeholder="Descreva o motivo pelo qual o pedido está sendo retornado para análise..."
                        disabled={isSubmitting}
                    />
                    <small className={styles.fieldHint}>
                        Esta informação será registrada no histórico do pedido e enviada ao criador e aprovadores.
                    </small>
                </div>

                <div className={styles.returnActions}>
                    <button
                        onClick={() => setShowConfirmModal(true)}
                        className={styles.returnButton}
                        disabled={isSubmitting}
                    >
                        <FiRefreshCw size={18} /> Retornar para Análise
                    </button>
                    <button
                        onClick={() => {
                            setOrderData(null);
                            setClientData(null);
                            setOrderHistory([]);
                            setSearchCode('');
                            setFormStatus({ message: '', type: '' });
                            setAnalysisReason('');
                        }}
                        className={styles.clearButton}
                        disabled={isSubmitting}
                    >
                        <FiX size={18} /> Limpar
                    </button>
                </div>

                {/* Informação sobre edição/exclusão */}
                <div className={styles.infoNote} style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    background: '#e3f2fd',
                    borderRadius: '8px',
                    borderLeft: '4px solid #0d47a1',
                    fontSize: '14px',
                    color: '#0d47a1'
                }}>
                    <FiInfo size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    <span>
                        <strong>Nota:</strong> Após o retorno, o pedido poderá ser <strong>editado</strong> ou <strong>excluído</strong> na página de Pedidos de Venda.
                    </span>
                </div>
            </div>
        );
    };

    // ============================================
    // 2.10 RENDERIZAÇÃO PRINCIPAL
    // ============================================
    return (
        <div className={styles.pageContainer}>
            {/* Toast Container */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer} aria-live="polite">
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Modal */}
            {renderConfirmModal()}

            {/* Header */}
            <PageHeader
                title="Retorno de Pedido para Análise"
                subtitle="Retorne pedidos para análise quando necessário"
            />

            {/* Search Section */}
            <div className={styles.searchSection}>
                <div className={styles.searchCard}>
                    <div className={styles.searchTitle}>
                        <FiSearch size={20} />
                        <span>Buscar Pedido</span>
                    </div>
                    <div className={styles.searchForm}>
                        <div className={styles.searchInputGroup}>
                            <input
                                type="text"
                                value={searchCode}
                                onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                                onKeyPress={handleKeyPress}
                                placeholder="Digite o código do pedido (ex: PV-1333)"
                                className={styles.searchInput}
                                disabled={isLoading || isSubmitting}
                                autoFocus
                            />
                            <button
                                onClick={searchOrderByCode}
                                className={styles.searchButton}
                                disabled={isLoading || !searchCode.trim() || isSubmitting}
                            >
                                {isLoading ? (
                                    <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                ) : (
                                    <FiSearch size={18} />
                                )}
                                Buscar
                            </button>
                        </div>
                        <div className={styles.searchHelp}>
                            <small>Digite o código completo do pedido de venda (ex: PV-1333)</small>
                        </div>
                    </div>

                    {/* Status Message */}
                    {formStatus.message && (
                        <div className={`${styles.alert} ${styles[formStatus.type]}`}>
                            <span className={styles.alertIcon}>
                                {formStatus.type === 'error' && <FiAlertCircle size={18} />}
                                {formStatus.type === 'success' && <FiCheck size={18} />}
                                {formStatus.type === 'warning' && <FiInfo size={18} />}
                                {formStatus.type === 'info' && <FiInfo size={18} />}
                            </span>
                            {formStatus.message}
                        </div>
                    )}
                </div>
            </div>

            {/* Order Details */}
            {orderData && renderOrderDetails()}

            {/* Empty State - No Order Found */}
            {orderNotFound && !orderData && !isLoading && (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                        <FiSearch size={48} />
                    </div>
                    <h3>Pedido não encontrado</h3>
                    <p>
                        {formStatus.message || 'Verifique o código digitado e tente novamente.'}
                    </p>
                    <div className={styles.emptyActions}>
                        <button
                            onClick={() => {
                                setSearchCode('');
                                setOrderNotFound(false);
                                setFormStatus({ message: '', type: '' });
                            }}
                            className={styles.secondaryButton}
                        >
                            <FiArrowLeft size={18} /> Voltar
                        </button>
                    </div>
                </div>
            )}

            {/* Instructions */}
            {!orderData && !orderNotFound && !isLoading && (
                <div className={styles.instructionsCard}>
                    <div className={styles.instructionsHeader}>
                        <FiInfo size={20} />
                        <span>Como funciona</span>
                    </div>
                    <ul className={styles.instructionsList}>
                        <li>
                            <strong>1. Busque o pedido</strong>
                            <span>Digite o código do pedido de venda no campo acima e clique em "Buscar".</span>
                        </li>
                        <li>
                            <strong>2. Verifique os detalhes</strong>
                            <span>Confirme as informações do pedido, produtos e histórico de status.</span>
                        </li>
                        <li>
                            <strong>3. Informe o motivo</strong>
                            <span>Descreva detalhadamente o motivo pelo qual o pedido está sendo retornado para análise.</span>
                        </li>
                        <li>
                            <strong>4. Confirme o retorno</strong>
                            <span>Clique em "Retornar para Análise" e confirme a ação.</span>
                        </li>
                    </ul>
                    <div className={styles.instructionsNote}>
                        <strong>⚠️ Atenção:</strong>
                        <span>Apenas pedidos com status <strong>Pendente Aprovação, Aprovado, Em Produção, Enviado ou Entregue</strong> podem ser retornados para análise.</span>
                    </div>
                    <div className={styles.instructionsNote} style={{ marginTop: '8px', background: '#e3f2fd', borderLeftColor: '#0d47a1' }}>
                        <strong>💡 Dica:</strong>
                        <span>Após o retorno para análise, o pedido ficará com status <strong>"Retornado para Análise"</strong> e poderá ser <strong>editado</strong> ou <strong>excluído</strong> na página de Pedidos de Venda.</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderReturnAnalysisPage;